export interface User {
  id: string;
  email: string;
  full_name: string;
  organization?: string;
  phone?: string;
  /** Set via the WhatsApp OTP flow; free tier needs it before the 2nd creation. */
  phone_verified?: boolean;
  subscription_tier: 'free' | 'junior' | 'pro' | 'firm' | 'firm_pro';
  monthly_jobs_limit: number;
  monthly_jobs_used: number;
  /** Direct (memoless) drafts used this month. */
  monthly_drafts_used?: number;
  /** Free-tier direct-draft cap; null/undefined = unlimited (paid tiers). */
  monthly_drafts_limit?: number | null;
  /** Server-gated feature flags (e.g. eCourts CNR lookup). */
  features?: { cnr_lookup?: boolean };
  /** Evening & morning hearing digests by email (Practice). */
  practice_email_reminders?: boolean;
  created_at: string;
}

export interface ResearchJob {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress_percent: number;
  current_step?: string;
  input_type: 'text' | 'voice' | 'document';
  jurisdiction?: string;
  law_domain?: string;
  /** Language the memo is written in: 'en' | 'hi' | 'ml'. */
  output_language?: string;
  /** Matter (client/case workspace) this job is filed under. */
  matter_id?: string | null;
  research_output_md?: string;
  research_output_html?: string;
  ai_model: string;
  /** Linked citations found in the memo (badge denominator). */
  citations_total?: number;
  /** Citations whose source URL resolved on link-check. */
  citations_verified?: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface Document {
  id: string;
  original_filename: string;
  file_size_bytes: number;
  mime_type: string;
  processing_status: string;
  created_at: string;
  /** Set by the backend when the stored file is downloadable (uploads on
      Render are ephemeral, so this is often absent). */
  download_url?: string | null;
}

export type InteractionMode = 'chat' | 'debate';

/** Registry-driven — the full list comes from GET /drafts/types. */
export type DraftType = string;

export interface DraftWarning {
  id: string;
  severity: string;
  message: string;
}

export interface Draft {
  id: string;
  /** null = direct (memoless) draft. */
  job_id: string | null;
  matter_id?: string | null;
  /** Document the draft was written against (direct drafts). */
  input_document_id?: string | null;
  draft_type: DraftType;
  title: string;
  /** Present on GET one / POST; omitted in list responses. */
  content_md?: string;
  /** Statutory-check warnings (direct drafts). */
  warnings?: DraftWarning[];
  created_at: string;
  /** Later than created_at ⇒ the draft was manually edited on the platform. */
  updated_at?: string | null;
}

export interface DraftFieldSpec {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'amount' | 'select';
  required: boolean;
  placeholder: string;
  help: string;
  options: string[];
}

export interface DraftTypeInfo {
  type: string;
  label: string;
  blurb: string;
  category: 'notices' | 'criminal' | 'civil' | 'consumer' | 'forms';
  citation_policy: 'none' | 'optional_light';
  /** Non-empty = attaching a source document genuinely helps this type. */
  attachment_hint: string;
  fields: DraftFieldSpec[];
}

/** One turn of the conversational drafting intake (POST /drafts/intake-chat). */
export interface DraftIntakeChatResponse {
  reply: string;
  ready: boolean;
  /** Cumulative field values gathered so far, keyed by DraftFieldSpec keys. */
  fields: Record<string, string>;
  instructions?: string | null;
}

/** Fallback labels for the legacy five (used before the registry loads). */
export const DRAFT_TYPE_LABELS: Record<string, string> = {
  legal_notice: 'Legal Notice',
  reply_notice: 'Reply to Notice',
  petition: 'Petition Skeleton',
  complaint: 'Complaint',
  rti_application: 'RTI Application',
};

export interface Matter {
  id: string;
  title: string;
  client_name?: string | null;
  description?: string | null;
  created_at: string;
  updated_at?: string | null;
  /** Present in list responses. */
  jobs_count?: number;
  documents_count?: number;
  // ---- Practice (case tracking) fields — set when the matter is tracked as a case ----
  client_id?: string | null;
  case_number?: string | null;
  cnr?: string | null;
  court_name?: string | null;
  court_type?: string | null;
  case_stage?: string | null;
  /** null = plain matter (not tracked as a case). */
  case_status?: 'active' | 'disposed' | 'archived' | null;
  party_role?: string | null;
  opposite_party?: string | null;
  next_hearing_date?: string | null;
  next_hearing_purpose?: string | null;
  /** Owner's name when this case was shared with the viewer. */
  shared_by?: string | null;
}

export interface MatterDraftItem {
  id: string;
  title: string;
  draft_type: string;
  job_id?: string | null;
  created_at: string;
}

export interface MatterDetailData extends Matter {
  jobs: ResearchJob[];
  documents: {
    id: string;
    original_filename: string;
    file_size_bytes: number;
    mime_type: string;
    processing_status: string;
    created_at: string;
  }[];
  /** Drafts filed under this matter (added with Practice, July 2026). */
  drafts?: MatterDraftItem[];
}

export interface MemoMessage {
  id: string;
  job_id: string;
  mode: 'chat' | 'quiz' | 'debate';
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface MemoChatTurn {
  user_message: MemoMessage;
  assistant_message: MemoMessage;
  interactions_remaining: number | null;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
  memo_section?: string;
}

export interface QuizResponse {
  job_id: string;
  questions: QuizQuestion[];
}

export interface CreateJobRequest {
  /** Optional — when omitted the API infers a title from the description. */
  title?: string;
  description: string;
  input_type: 'text' | 'voice' | 'document';
  input_document_id?: string;
  /** Optional matter (client/case workspace) to file this research under. */
  matter_id?: string;
  jurisdiction?: string;
  law_domain?: string;
  /** Detected from the user's typed text ('en'|'hi'|'ml'); server falls back to
   *  detecting from the description when omitted. */
  output_language?: string;
}

export interface FrameChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface FrameQuestionResponse {
  reply: string;
  ready: boolean;
  question?: string | null;
  /** Framed question in the user's language (display); `question` stays English. */
  question_display?: string | null;
  jurisdiction?: string | null;
  law_domain?: string | null;
}

// ---------------------------------------------------------------------------
// Practice (case / hearing / client / task tracking) — July 2026
// ---------------------------------------------------------------------------

export type HearingStatus = 'scheduled' | 'held' | 'adjourned' | 'cancelled';
export type PrepStatus = 'todo' | 'in_progress' | 'ready';
export type HearingSource = 'manual' | 'assistant' | 'ecourts';
export type CaseStatus = 'active' | 'disposed' | 'archived';
export type TaskStatus = 'open' | 'in_progress' | 'done' | 'cancelled';
export type TaskType =
  | 'drafting'
  | 'research'
  | 'enquiry'
  | 'argument_prep'
  | 'document_verification'
  | 'filing'
  | 'other';

/** Row of the merged Cases list (GET /practice/cases). */
export interface CaseListItem {
  id: string;
  title: string;
  client_name?: string | null;
  client_id?: string | null;
  case_number?: string | null;
  cnr?: string | null;
  court_name?: string | null;
  court_type?: string | null;
  case_stage?: string | null;
  /** null = plain matter (not tracked as a case). */
  case_status?: CaseStatus | null;
  party_role?: string | null;
  opposite_party?: string | null;
  next_hearing_date?: string | null;
  next_hearing_purpose?: string | null;
  updated_at?: string | null;
  created_at?: string;
  /** Owner's name when this case was shared with the viewer. */
  shared_by?: string | null;
}

export interface Hearing {
  id: string;
  matter_id: string;
  hearing_date: string;
  purpose?: string | null;
  status: HearingStatus;
  outcome?: string | null;
  next_date?: string | null;
  prep_note?: string | null;
  prep_status: PrepStatus;
  source: HearingSource;
  verified: boolean;
  created_at?: string;
  updated_at?: string | null;
}

export interface Task {
  id: string;
  user_id: string;
  matter_id?: string | null;
  title: string;
  task_type: TaskType;
  details?: string | null;
  due_date?: string | null;
  status: TaskStatus;
  assignee_user_id?: string | null;
  completed_at?: string | null;
  created_at?: string;
  matter_title?: string | null;
  assignee_name?: string | null;
  owner_name?: string | null;
}

export interface BoardHearingItem {
  hearing_id: string;
  matter_id: string;
  case_title: string;
  client_name?: string | null;
  court_name?: string | null;
  case_number?: string | null;
  case_stage?: string | null;
  hearing_date: string;
  purpose?: string | null;
  status: HearingStatus;
  prep_status: PrepStatus;
  prep_note?: string | null;
  source: HearingSource;
  verified: boolean;
  shared_by?: string | null;
}

export interface CallListItem {
  matter_id: string;
  case_title: string;
  court_name?: string | null;
  hearing_date: string;
  purpose?: string | null;
  client?: { id: string; name: string; phone?: string | null; whatsapp?: string | null } | null;
  client_name?: string | null;
}

export interface BoardResponse {
  as_of: string;
  today: string;
  today_hearings: BoardHearingItem[];
  tomorrow_hearings: BoardHearingItem[];
  needs_update: BoardHearingItem[];
  no_date_cases: {
    matter_id: string;
    case_title: string;
    court_name?: string | null;
    last_hearing_date?: string | null;
  }[];
  call_list: CallListItem[];
  tasks_due: Task[];
  assigned_to_me: Task[];
}

export interface Collaborator {
  id: string;
  matter_id: string;
  invited_email: string;
  collaborator_user_id?: string | null;
  collaborator_name?: string | null;
  role: string;
  status: 'pending' | 'active';
  created_at?: string;
}

export interface Client {
  id: string;
  name: string;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  address?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface ClientListItem extends Client {
  cases_count: number;
}

export interface ClientDetailData extends Client {
  /** Loaded separately via GET /clients/{id}/cases. */
  cases: CaseListItem[];
}

/** Prefill returned by POST /practice/cases/cnr-lookup. */
export interface CnrLookupResponse {
  prefill: {
    cnr: string;
    title?: string | null;
    case_number?: string | null;
    court_name?: string | null;
    case_type?: string | null;
    case_year?: string | number | null;
    case_stage?: string | null;
    state_code?: string | null;
    district?: string | null;
    opposite_party?: string | null;
    next_hearing_date?: string | null;
  };
  warnings: string[];
}

export type AssistantArtifactType =
  | 'board'
  | 'case_detail'
  | 'hearings'
  | 'tasks'
  | 'call_list'
  | 'cases';

export interface AssistantArtifact {
  type: AssistantArtifactType;
  rows: any[];
  applied_filters?: Record<string, any> | null;
}

export interface AssistantAction {
  type: string;
  params: Record<string, any>;
}

export interface AssistantResponse {
  reply: string;
  intent: 'chat' | 'query' | 'action';
  artifact?: AssistantArtifact | null;
  ready: boolean;
  action?: AssistantAction | null;
}

export interface AssistantExecuteResponse {
  ok: boolean;
  message: string;
  entity_type?: string | null;
  entity?: any;
}

export interface CalendarToken {
  token: string;
  ics_url: string;
  webcal_url: string;
}
